import React, { useState } from "react";

import {
  CopyRegular,
  DeleteRegular,
  EditRegular,
  OpenRegular,
  PeopleTeamRegular,
  PersonRegular,
} from "@fluentui/react-icons";
import {
  Body2,
  Button,
  Card,
  CardProps,
  mergeClasses,
  Switch,
  Tooltip,
  useRestoreFocusTarget,
} from "@fluentui/react-components";

import { Stack } from "../stack";
import { YellowPadLogo } from "../yellowpad-logo";
import { DeletePlaybookDialog } from "../delete-playbook-dialog";
import { DuplicatePlaybookDialog } from "../duplicate-playbook-dialog";
import { PrivatizePlaybookDialog } from "../privatize-playbook-dialog";
import { usePlaybookCardStyles } from "./styles";

import { useAuth } from "@/context/auth";

import { Playbook } from "@/types/risk-analysis";

export interface PlaybookCardProps extends CardProps {
  playbook: Playbook;
  onOpen: () => void;
  onEdit: () => void;
}

export function PlaybookCard({ playbook, className, onOpen, onEdit, ...props }: PlaybookCardProps) {
  const { user: auth } = useAuth();
  const styles = usePlaybookCardStyles();

  const restoreDeleteTargetAttribute = useRestoreFocusTarget();
  const restoreDuplicateTargetAttribute = useRestoreFocusTarget();
  const restorePrivatizeTargetAttribute = useRestoreFocusTarget();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState<boolean>(false);
  const [privatizeDialogOpen, setPrivatizeDialogOpen] = useState<boolean>(false);

  const isCreator = playbook.creatorUserId === auth.user.id;
  const switchElement = (
    <Switch
      label="Private"
      labelPosition="before"
      disabled={!isCreator}
      checked={!!playbook.ownerUserId && playbook.ownerUserId === auth.user.id}
      onChange={() => setPrivatizeDialogOpen(true)}
      {...restorePrivatizeTargetAttribute}
    />
  );

  const isEditable = playbook.editable || playbook.creatorUserId === auth.user.id;
  const editButtonElement = (
    <Button
      appearance="primary"
      icon={<EditRegular />}
      aria-label="Edit playbook"
      disabled={!isEditable}
      onClick={onEdit}
    />
  );
  const deleteButtonElement = (
    <Button
      appearance="primary"
      icon={<DeleteRegular />}
      aria-label="Delete playbook"
      disabled={!isEditable}
      onClick={() => setDeleteDialogOpen(true)}
      {...restoreDeleteTargetAttribute}
    />
  );

  return (
    <Card className={mergeClasses(styles.card, className)} {...props}>
      <Stack align="center" justify="between">
        <Stack align="center" gap="0.25rem">
          <Tooltip
            relationship="label"
            content={playbook.ownerUserId === auth.user.id ? "Private playbook" : "Public playbook"}
          >
            {playbook.ownerUserId === auth.user.id ? (
              <PersonRegular fontSize={20} />
            ) : (
              <PeopleTeamRegular fontSize={20} />
            )}
          </Tooltip>
          {playbook.originalPlaybookId && (
            <Tooltip relationship="label" content="Duplicate from a Yellowpad playbook">
              <YellowPadLogo />
            </Tooltip>
          )}
          <Body2 className={styles.name}>{playbook.name}</Body2>
        </Stack>
        <Stack align="center" gap="0.5rem">
          <Tooltip relationship="label" content="Open playbook">
            <Button appearance="primary" icon={<OpenRegular />} aria-label="Open playbook" onClick={onOpen} />
          </Tooltip>
          <Tooltip relationship="label" content="Duplicate playbook">
            <Button
              appearance="primary"
              icon={<CopyRegular />}
              aria-label="Duplicate playbook"
              onClick={() => setDuplicateDialogOpen(true)}
              {...restoreDuplicateTargetAttribute}
            />
          </Tooltip>
          {isEditable ? (
            <Tooltip relationship="label" content="Delete playbook">
              {deleteButtonElement}
            </Tooltip>
          ) : (
            deleteButtonElement
          )}
          {isEditable ? (
            <Tooltip relationship="label" content="Edit playbook">
              {editButtonElement}
            </Tooltip>
          ) : (
            editButtonElement
          )}
        </Stack>
      </Stack>
      <Stack align="center" justify="between" className={styles.cardSwitchRow}>
        <Body2>Last updated on {new Date(playbook.updatedAt).toLocaleDateString()}</Body2>
        {!isCreator ? (
          <Tooltip relationship="label" content="You can only change the public status of a playbook you created">
            {switchElement}
          </Tooltip>
        ) : (
          switchElement
        )}
      </Stack>
      <DeletePlaybookDialog
        playbook={playbook}
        open={deleteDialogOpen}
        onOpenChange={(_, data) => setDeleteDialogOpen(data.open)}
      />
      <DuplicatePlaybookDialog
        playbook={playbook}
        open={duplicateDialogOpen}
        onOpenChange={(_, data) => setDuplicateDialogOpen(data.open)}
      />
      <PrivatizePlaybookDialog
        playbook={playbook}
        open={privatizeDialogOpen}
        onOpenChange={(_, data) => setPrivatizeDialogOpen(data.open)}
      />
    </Card>
  );
}
